import React, { useEffect, useRef, useState, useCallback } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "@/lib/toast";
import { useAuth } from "@/context/AuthContext";
import { hasSuperPrivileges } from "@/lib/accountRole";
import {
  Award, Plus, Trash2, Upload, X, Star, ShieldCheck, ShieldAlert, Users, Loader2, Save, Eye,
  ChevronLeft, ChevronRight, ChevronUp, ChevronDown, GripVertical, Pencil, Check, AlertTriangle,
  Fingerprint, MailCheck, Tag, Gift, ExternalLink,
} from "lucide-react";

const SEL = "w-full bg-input border border-sniper focus:border-olive outline-none px-2.5 py-2 text-foreground font-body text-sm";
const LBL = "block font-mono text-[10px] uppercase tracking-[1.5px] text-secondary-foreground mb-1.5";
const BTN = "font-mono uppercase text-[10px] font-bold px-3 py-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed";
const MAX_PHOTOS = 12;
const SOCIALS = [["instagram", "Instagram"], ["youtube", "YouTube"], ["x", "X"], ["tiktok", "TikTok"], ["facebook", "Facebook"], ["website", "Website"]];
const STATUS_TONE = { draft: "text-muted-foreground border-sniper", published: "text-[#4E8A1F] border-[#4E8A1F]/50", retired: "text-blaze border-blaze/50" };

const EMPTY = {
  entry_type: "individual", display_name: "", team_members: [], location: "", bio: "",
  photos: [], specialties: [], socials: {}, linked_username: "", photo_rights_confirmed: false, stats: [],
};

function wordCount(s) { return (s || "").trim() ? s.trim().split(/\s+/).length : 0; }

const isVerifiedAcct = (a) => a?.identity_status === "verified" || a?.verification_state === "VERIFIED";

// Item 5 — inline summary + admin quick actions for the linked OpticSwap account, right on the
// ambassador form so we never have to bounce to /admin/accounts to chase the sign-up/verify. All
// four actions hit the same per-account endpoints the Accounts drawer uses; Comp Tier is super-only.
function LinkedAccountActions({ userId, onRefetch }) {
  const { user: viewer } = useAuth();
  const canComp = hasSuperPrivileges(viewer?.role);
  const [acct, setAcct] = useState(null);
  const [busy, setBusy] = useState("");
  const [panel, setPanel] = useState(null);       // null | "type" | "comp"
  const [typeSel, setTypeSel] = useState("individual");
  const [bizName, setBizName] = useState("");
  const [tierSel, setTierSel] = useState("preferred");
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/admin/accounts/${userId}`); setAcct(data); setTypeSel(data.account_type === "business" ? "business" : "individual"); setBizName(data.business_name || ""); }
    catch { setAcct(null); }
  }, [userId]);
  useEffect(() => { if (userId) load(); }, [userId, load]);

  const refresh = async () => { await load(); if (onRefetch) onRefetch(); };

  const requestId = async () => {
    setBusy("id");
    try { await api.post(`/admin/accounts/${userId}/reverify-nudge`); toast.success("ID-verification prompt sent."); await refresh(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail) || "Could not send prompt."); }
    finally { setBusy(""); }
  };
  const resendEmail = async () => {
    setBusy("email");
    try { await api.post(`/admin/accounts/${userId}/resend-email-verification`); toast.success("Verification email resent."); await refresh(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail) || "Could not resend email."); }
    finally { setBusy(""); }
  };
  const applyType = async () => {
    if (typeSel === "business" && bizName.trim().length < 2) { toast.error("Enter the legal business name."); return; }
    setBusy("type");
    try {
      await api.post(`/admin/accounts/${userId}/account-type`, {
        account_type: typeSel, business_name: typeSel === "business" ? bizName.trim() : undefined,
        reason: "Set via ambassador linked-account quick action.",
      });
      toast.success(`Account type set to ${typeSel}.`);
      setPanel(null); await refresh();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Could not set account type."); }
    finally { setBusy(""); }
  };
  const applyComp = async () => {
    if (reason.trim().length < 20) { toast.error("A reason of at least 20 characters is required."); return; }
    setBusy("comp");
    try {
      await api.post(`/admin/accounts/${userId}/comp`, { tier: tierSel, reason: reason.trim() });
      toast.success(`Comped ${tierSel} tier.`);
      setPanel(null); setReason(""); await refresh();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Could not comp tier."); }
    finally { setBusy(""); }
  };

  if (!acct) return null;
  const idVerified = acct.identity_status === "verified" || acct.identity_verified;
  const emailVerified = acct.email_verified;
  const mem = acct.membership || {};
  const tierLabel = mem.tier ? `${mem.tier}${mem.comped ? " (comped)" : ""}` : "no membership";
  const chip = "font-mono text-[9px] uppercase px-1.5 py-0.5 border";
  const qbtn = "font-mono text-[10px] uppercase px-2.5 py-1.5 border transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1";

  return (
    <div className="mt-2 border border-sniper bg-surface/40 p-3 space-y-3" data-testid="ambassador-linked-actions">
      {/* Inline summary */}
      <div className="flex flex-wrap items-center gap-2" data-testid="ambassador-linked-summary">
        <span className={`${chip} border-sniper text-secondary-foreground`}>{acct.account_type || "unclassified"}</span>
        <span className={`${chip} ${idVerified ? "border-[#4E8A1F]/50 text-[#4E8A1F]" : "border-[#E8C46A]/50 text-[#E8C46A]"}`} data-testid="ambassador-linked-idstate">
          {idVerified ? "ID verified" : `ID ${acct.identity_status || "unverified"}`}
        </span>
        <span className={`${chip} ${emailVerified ? "border-[#4E8A1F]/50 text-[#4E8A1F]" : "border-[#E8C46A]/50 text-[#E8C46A]"}`} data-testid="ambassador-linked-emailstate">
          {emailVerified ? "email verified" : "email unverified"}
        </span>
        <span className={`${chip} border-sniper text-secondary-foreground`} data-testid="ambassador-linked-tier">{tierLabel}</span>
      </div>

      {/* Quick actions */}
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={requestId} disabled={busy === "id"} data-testid="ambassador-linked-request-id"
          className={`${qbtn} border-steel/60 text-steel hover:bg-steel/10`}>
          <Fingerprint className="w-3 h-3" /> {busy === "id" ? "Sending…" : "Request ID"}
        </button>
        <button type="button" onClick={resendEmail} disabled={busy === "email" || emailVerified} title={emailVerified ? "Email already verified" : ""} data-testid="ambassador-linked-resend-email"
          className={`${qbtn} border-[#d3a24e]/60 text-[#d3a24e] hover:bg-[#d3a24e]/10`}>
          <MailCheck className="w-3 h-3" /> {busy === "email" ? "Sending…" : "Resend Email"}
        </button>
        <button type="button" onClick={() => setPanel(panel === "type" ? null : "type")} data-testid="ambassador-linked-set-type"
          className={`${qbtn} border-sniper text-foreground hover:border-olive-bright`}>
          <Tag className="w-3 h-3" /> Set Account Type
        </button>
        <button type="button" onClick={() => setPanel(panel === "comp" ? null : "comp")} disabled={!canComp} title={canComp ? "" : "Requires super admin"} data-testid="ambassador-linked-comp-tier"
          className={`${qbtn} border-volt/50 text-volt hover:bg-volt/10`}>
          <Gift className="w-3 h-3" /> Comp Tier
        </button>
      </div>

      {/* Set Account Type panel */}
      {panel === "type" && (
        <div className="border border-sniper p-2.5 space-y-2" data-testid="ambassador-linked-type-panel">
          <div className="flex items-center gap-2">
            <select value={typeSel} onChange={(e) => setTypeSel(e.target.value)} className={SEL + " w-auto"} data-testid="ambassador-linked-type-select">
              <option value="individual">Individual</option>
              <option value="business">Business</option>
            </select>
            {typeSel === "business" && (
              <input value={bizName} onChange={(e) => setBizName(e.target.value)} placeholder="Legal business name" className={SEL} data-testid="ambassador-linked-biz-name" />
            )}
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={applyType} disabled={busy === "type"} data-testid="ambassador-linked-type-apply" className={`${BTN} bg-olive text-white hover:bg-olive-bright`}>{busy === "type" ? "Saving…" : "Apply"}</button>
            <button type="button" onClick={() => setPanel(null)} data-testid="ambassador-linked-type-cancel" className={`${BTN} border border-sniper text-muted-foreground hover:text-foreground`}>Cancel</button>
          </div>
        </div>
      )}

      {/* Comp Tier panel (super only) */}
      {panel === "comp" && (
        <div className="border border-volt/40 bg-volt/5 p-2.5 space-y-2" data-testid="ambassador-linked-comp-panel">
          <p className="font-mono text-[10px] uppercase text-volt flex items-center gap-1"><Gift className="w-3 h-3" /> Comped tier — real money given away, audited.</p>
          <select value={tierSel} onChange={(e) => setTierSel(e.target.value)} className={SEL + " w-auto"} data-testid="ambassador-linked-comp-tier-select">
            <option value="standard">Standard</option>
            <option value="preferred">Preferred</option>
            <option value="elite">Elite</option>
          </select>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Reason (min 20 chars) — recorded in Audit Events" className={SEL} data-testid="ambassador-linked-comp-reason" />
          {reason.trim().length < 20 && <p className="font-mono text-[9px] uppercase text-volt">Reason must be ≥20 chars ({reason.trim().length}/20).</p>}
          <div className="flex gap-2">
            <button type="button" onClick={applyComp} disabled={busy === "comp" || reason.trim().length < 20} data-testid="ambassador-linked-comp-apply" className={`${BTN} bg-volt text-black hover:opacity-90`}>{busy === "comp" ? "Comping…" : "Comp tier"}</button>
            <button type="button" onClick={() => setPanel(null)} data-testid="ambassador-linked-comp-cancel" className={`${BTN} border border-sniper text-muted-foreground hover:text-foreground`}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// Debounced type-ahead picker over ALL platform accounts. Selecting binds instantly (status shown
// from the picked account, no save-and-recheck round trip). Empty = "link later" fallback.
function LinkedAccountPicker({ value, initial, onChange }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState(null); // full account object chosen this session

  useEffect(() => {
    const q = query.trim();
    if (!q) { setResults([]); setLoading(false); return; }
    setLoading(true);
    const t = setTimeout(() => {
      api.get(`/admin/accounts?search=${encodeURIComponent(q)}&limit=6`)
        .then(({ data }) => setResults(data.accounts || []))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const pick = (acc) => { setPicked(acc); onChange(acc.username); setQuery(""); setResults([]); };
  const clear = () => { setPicked(null); onChange(""); setQuery(""); setResults([]); };

  // Resolve the status of the bound username: freshly-picked account wins, else the saved snapshot.
  const savedMatch = !!initial.linked_username && initial.linked_username === value;
  const verified = picked ? isVerifiedAcct(picked) : savedMatch ? initial.linked_verified : undefined;
  const exists = picked ? true : savedMatch ? initial.linked_exists : undefined;
  const meta = picked || { username: value };
  const uid = picked?.user_id || initial.linked_user_id;

  if (value) {
    return (
      <div className="space-y-0" data-testid="ambassador-linked-wrap">
        <div className="border border-sniper bg-input px-3 py-2.5 flex items-center gap-3" data-testid="ambassador-linked-selected">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              {uid ? (
                <a href={`/admin/accounts?open=${uid}`} target="_blank" rel="noreferrer"
                  className="font-mono text-xs text-olive-bright hover:underline inline-flex items-center gap-1" data-testid="ambassador-linked-handle-link">
                  @{meta.username} <ExternalLink className="w-3 h-3" />
                </a>
              ) : (
                <span className="font-mono text-xs text-foreground">@{meta.username}</span>
              )}
              {verified === true ? (
                <span className="font-mono text-[9px] uppercase text-[#4E8A1F] inline-flex items-center gap-1" data-testid="ambassador-linked-status"><ShieldCheck className="w-3 h-3" /> ID verified</span>
              ) : verified === false && exists ? (
                <span className="font-mono text-[9px] uppercase text-[#E8C46A] inline-flex items-center gap-1" data-testid="ambassador-linked-status"><ShieldAlert className="w-3 h-3" /> Not ID-verified</span>
              ) : exists === false ? (
                <span className="font-mono text-[9px] uppercase text-blaze inline-flex items-center gap-1" data-testid="ambassador-linked-status"><ShieldAlert className="w-3 h-3" /> No such account</span>
              ) : (
                <span className="font-mono text-[9px] uppercase text-muted-foreground" data-testid="ambassador-linked-status">Save to check</span>
              )}
            </div>
            {(meta.name || meta.email) && (
              <div className="font-mono text-[10px] text-muted-foreground truncate mt-0.5">{meta.name || "—"}{meta.email ? ` · ${meta.email}` : ""}</div>
            )}
          </div>
          <button type="button" onClick={clear} title="Unlink account" data-testid="ambassador-linked-clear" className="text-muted-foreground hover:text-blaze shrink-0"><X className="w-4 h-4" /></button>
        </div>
        {uid && <LinkedAccountActions userId={uid} onRefetch={() => setPicked(null)} />}
      </div>
    );
  }

  return (
    <div className="relative" data-testid="ambassador-linked-picker">
      <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search accounts by handle, name, or email…" className={SEL} data-testid="ambassador-linked-search" />
      {query.trim() && (loading || results.length >= 0) && (
        <div className="absolute z-20 left-0 right-0 mt-1 border border-sniper bg-surface divide-y divide-sniper max-h-60 overflow-y-auto shadow-lg" data-testid="ambassador-linked-results">
          {loading && <div className="px-3 py-2 font-mono text-[10px] uppercase text-muted-foreground flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Searching…</div>}
          {!loading && results.length === 0 && <div className="px-3 py-2 font-mono text-[10px] uppercase text-muted-foreground">No matching accounts</div>}
          {!loading && results.map((r) => {
            const rv = isVerifiedAcct(r);
            return (
              <button key={r.user_id} type="button" onClick={() => pick(r)} data-testid={`ambassador-linked-pick-${r.username}`}
                className="w-full text-left px-3 py-2 hover:bg-white/5 transition-colors">
                <div className="flex items-center gap-1.5">
                  <span className="font-mono text-xs text-foreground truncate">@{r.username}</span>
                  {rv
                    ? <span className="inline-flex items-center gap-0.5 font-mono text-[8px] uppercase text-[#4E8A1F] shrink-0"><ShieldCheck className="w-3 h-3" /> verified</span>
                    : <span className="inline-flex items-center gap-0.5 font-mono text-[8px] uppercase text-[#E8C46A] shrink-0"><ShieldAlert className="w-3 h-3" /> unverified</span>}
                </div>
                <div className="font-mono text-[10px] text-muted-foreground truncate">{r.name || "—"}{r.email ? ` · ${r.email}` : ""}</div>
              </button>
            );
          })}
        </div>
      )}
      <p className="font-mono text-[10px] text-muted-foreground mt-1.5" data-testid="ambassador-linked-fallback">
        No account yet? Leave this empty — you can publish now and link the account later. The public ID-verified badge appears automatically once a linked account is verified.
      </p>
    </div>
  );
}

function Editor({ initial, specialties, onSaved, onClose }) {
  const isNew = !initial.ambassador_id;
  const [form, setForm] = useState({ ...EMPTY, ...initial, socials: { ...(initial.socials || {}) } });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const fileRef = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const wc = wordCount(form.bio);
  const missing = initial.missing_for_publish || [];

  const toggleSpec = (s) => {
    setForm((f) => {
      const has = f.specialties.includes(s);
      if (has) return { ...f, specialties: f.specialties.filter((x) => x !== s) };
      if (f.specialties.length >= 3) { toast.error("Up to 3 specialties."); return f; }
      return { ...f, specialties: [...f.specialties, s] };
    });
  };

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (form.photos.length + files.length > MAX_PHOTOS) { toast.error(`Max ${MAX_PHOTOS} photos.`); return; }
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/ambassadors/upload-photo", fd);
        setForm((f) => ({ ...f, photos: [...f.photos, data.url] }));
      }
      toast.success("Photo uploaded");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removePhoto = (i) => setForm((f) => ({ ...f, photos: f.photos.filter((_, idx) => idx !== i) }));

  const addStat = () => setForm((f) => ({ ...f, stats: [...(f.stats || []), { label: "", value: "" }] }));
  const setStat = (i, k, v) => setForm((f) => { const s = [...(f.stats || [])]; s[i] = { ...s[i], [k]: v }; return { ...f, stats: s }; });
  const removeStat = (i) => setForm((f) => ({ ...f, stats: (f.stats || []).filter((_, idx) => idx !== i) }));
  // Reorder within the photo strip. Position 0 is always the hero and updates live.
  const movePhoto = (from, to) => setForm((f) => {
    const n = f.photos.length;
    if (from == null || to < 0 || to >= n || from === to) return f;
    const p = [...f.photos];
    const [x] = p.splice(from, 1);
    p.splice(to, 0, x);
    return { ...f, photos: p };
  });
  const onPhotoDrop = (to) => { movePhoto(dragIndex, to); setDragIndex(null); };

  const payload = () => ({
    ...form,
    team_members: typeof form.team_members === "string"
      ? form.team_members.split(",").map((s) => s.trim()).filter(Boolean)
      : form.team_members,
  });

  const save = async () => {
    if (!form.display_name.trim()) { toast.error("Display name is required."); return; }
    setBusy(true);
    try {
      let doc;
      if (isNew) doc = (await api.post("/admin/ambassadors", payload())).data;
      else doc = (await api.put(`/admin/ambassadors/${initial.ambassador_id}`, payload())).data;
      toast.success(isNew ? "Draft created" : "Saved");
      onSaved(doc);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const changeStatus = async (status) => {
    if (isNew) { toast.error("Save the draft first."); return; }
    setBusy(true);
    try {
      await api.post(`/admin/ambassadors/${initial.ambassador_id}/status`, { status });
      toast.success(`Set to ${status}`);
      onSaved(null);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async () => {
    if (isNew) { onClose(); return; }
    if (!window.confirm("Delete this ambassador entry? This cannot be undone.")) return;
    setBusy(true);
    try {
      await api.delete(`/admin/ambassadors/${initial.ambassador_id}`);
      toast.success("Deleted"); onSaved(null);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const teamStr = Array.isArray(form.team_members) ? form.team_members.join(", ") : form.team_members;

  return (
    <div className="border border-sniper bg-surface" data-testid="ambassador-editor">
      <div className="flex items-center justify-between px-4 py-3 border-b border-sniper">
        <h3 className="font-heading uppercase tracking-tight text-foreground">{isNew ? "New ambassador" : `Edit — ${initial.display_name || "(unnamed)"}`}</h3>
        <button onClick={onClose} data-testid="ambassador-editor-close" className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
      </div>
      <div className="p-4 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className={LBL}>Entry type</label>
            <select value={form.entry_type} onChange={(e) => set("entry_type", e.target.value)} className={SEL} data-testid="ambassador-entry-type">
              <option value="individual">Individual</option>
              <option value="team">Team (group card)</option>
            </select>
          </div>
          <div>
            <label className={LBL}>Display name <span className="text-[#E8C46A]">*</span></label>
            <input value={form.display_name} onChange={(e) => set("display_name", e.target.value)} className={SEL} placeholder="e.g. KC or Ridge & Valley Pursuits" data-testid="ambassador-display-name" />
          </div>
        </div>

        {form.entry_type === "team" && (
          <div>
            <label className={LBL}>Team members (comma-separated)</label>
            <input value={teamStr} onChange={(e) => set("team_members", e.target.value)} className={SEL} placeholder="Alex R., Jordan M., Sam T." data-testid="ambassador-team-members" />
          </div>
        )}

        <div>
          <label className={LBL}>Location (City, ST)</label>
          <input value={form.location} onChange={(e) => set("location", e.target.value)} className={SEL} placeholder="Bozeman, MT" data-testid="ambassador-location" />
        </div>

        <div>
          <label className={LBL}>Bio <span className="text-[#E8C46A]">*</span> <span className="text-muted-foreground normal-case">— target 80–150 words · no "glass"</span></label>
          <textarea value={form.bio} onChange={(e) => set("bio", e.target.value)} rows={5} className={SEL} data-testid="ambassador-bio" />
          <span className={`font-mono text-[10px] ${wc < 80 || wc > 150 ? "text-[#E8C46A]" : "text-muted-foreground"}`} data-testid="ambassador-bio-count">{wc} words</span>
        </div>

        {/* Photos */}
        <div>
          <label className={LBL}>Photos (3–{MAX_PHOTOS}) <span className="text-[#E8C46A]">*</span> <span className="text-muted-foreground normal-case">— drag to reorder (or use the arrows) · position 1 is the 4:5 hero · empty slots show how many are left</span></label>
          <div className="grid grid-cols-6 gap-2" data-testid="ambassador-photos">
            {form.photos.map((p, i) => (
              <div key={`${p}-${i}`}
                draggable
                onDragStart={() => setDragIndex(i)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onPhotoDrop(i)}
                onDragEnd={() => setDragIndex(null)}
                className={`relative aspect-[4/5] border overflow-hidden group cursor-move transition-opacity ${dragIndex === i ? "opacity-40 border-olive-bright" : "border-sniper"}`}
                data-testid={`ambassador-photo-${i}`}>
                <img src={p} alt="" className="w-full h-full object-cover pointer-events-none" />
                {i === 0
                  ? <span className="absolute top-1 left-1 bg-[#C2571F] text-white font-mono text-[8px] uppercase px-1 py-0.5" data-testid={`ambassador-photo-hero-badge-${i}`}>Hero</span>
                  : <span className="absolute top-1 left-1 bg-black/60 text-white font-mono text-[8px] px-1 py-0.5" data-testid={`ambassador-photo-pos-${i}`}>{i + 1}</span>}
                <span className="absolute top-1 right-1 text-white/70 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"><GripVertical className="w-3 h-3" /></span>
                <div className="absolute inset-x-0 bottom-0 flex opacity-0 group-hover:opacity-100 transition-opacity">
                  <button type="button" onClick={() => movePhoto(i, i - 1)} disabled={i === 0} title="Move left" className="flex-1 bg-black/70 text-white py-1 flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed" data-testid={`ambassador-photo-left-${i}`}><ChevronLeft className="w-3 h-3" /></button>
                  <button type="button" onClick={() => movePhoto(i, i + 1)} disabled={i === form.photos.length - 1} title="Move right" className="flex-1 bg-black/70 text-white py-1 flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed" data-testid={`ambassador-photo-right-${i}`}><ChevronRight className="w-3 h-3" /></button>
                  <button type="button" onClick={() => removePhoto(i)} title="Remove" className="flex-1 bg-black/70 text-blaze py-1 flex items-center justify-center" data-testid={`ambassador-photo-remove-${i}`}><Trash2 className="w-3 h-3" /></button>
                </div>
              </div>
            ))}
            {Array.from({ length: MAX_PHOTOS - form.photos.length }).map((_, k) => {
              const slot = form.photos.length + k;
              const isNext = k === 0;
              return (
                <button key={`slot-${slot}`} type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                  className={`relative aspect-[4/5] border border-dashed flex flex-col items-center justify-center gap-1 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${isNext ? "border-olive-bright/60 text-foreground hover:border-olive-bright" : "border-sniper text-muted-foreground hover:border-olive-bright hover:text-foreground"}`}
                  data-testid={`ambassador-photo-slot-${slot}`}>
                  <span className="absolute top-1 left-1 font-mono text-[8px] text-muted-foreground/70">{slot + 1}</span>
                  {isNext && uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  <span className="font-mono text-[8px] uppercase tracking-[1px]">{isNext ? "Add" : "Empty"}</span>
                </button>
              );
            })}
          </div>
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={upload} />
        </div>

        {/* Specialties */}
        <div>
          <label className={LBL}>Specialties (1–3) <span className="text-[#E8C46A]">*</span> <span className="text-muted-foreground normal-case">— first one leads the chip row on the public card</span></label>
          <div className="flex flex-wrap gap-2" data-testid="ambassador-specialties">
            {specialties.map((s) => {
              const on = form.specialties.includes(s);
              const first = form.specialties[0] === s;
              return (
                <button key={s} onClick={() => toggleSpec(s)} data-testid={`ambassador-spec-${s}`}
                  className={`font-mono text-[10px] uppercase tracking-[1px] px-3 py-1.5 border transition-colors ${on ? "border-olive-bright text-volt bg-olive/10" : "border-sniper text-muted-foreground hover:text-foreground"}`}>
                  {first && <Star className="w-2.5 h-2.5 inline mr-1 -mt-0.5" />}{s}
                </button>
              );
            })}
          </div>
        </div>

        {/* Socials */}
        <div>
          <label className={LBL}>Social handles <span className="text-muted-foreground normal-case">— recommended</span></label>
          <div className="grid sm:grid-cols-2 gap-2" data-testid="ambassador-socials">
            {SOCIALS.map(([k, lbl]) => {
              if (k === "instagram") {
                const ig = Array.isArray(form.socials.instagram)
                  ? form.socials.instagram
                  : (form.socials.instagram ? [form.socials.instagram] : []);
                const setIg = (i, val) => {
                  const next = [ig[0] || "", ig[1] || ""];
                  next[i] = val;
                  set("socials", { ...form.socials, instagram: next });
                };
                return [
                  <input key="instagram" value={ig[0] || ""} onChange={(e) => setIg(0, e.target.value)}
                    className={SEL} placeholder="Instagram" data-testid="ambassador-social-instagram" />,
                  <input key="instagram-2" value={ig[1] || ""} onChange={(e) => setIg(1, e.target.value)}
                    className={SEL} placeholder="Instagram (2nd — optional)" data-testid="ambassador-social-instagram-2" />,
                ];
              }
              return (
                <input key={k} value={form.socials[k] || ""} onChange={(e) => set("socials", { ...form.socials, [k]: e.target.value })}
                  className={SEL} placeholder={lbl} data-testid={`ambassador-social-${k}`} />
              );
            })}
          </div>
        </div>

        {/* Field Map — quick stats (optional, up to 4) */}
        <div>
          <label className={LBL}>Field map / quick stats <span className="text-muted-foreground normal-case">— optional · up to 4 label → value pairs shown as a stat bar on the detail page (e.g. "Shooting Since" → "Age 12 · 20 yrs"). Leave blank to hide the bar.</span></label>
          <div className="space-y-2" data-testid="ambassador-stats">
            {(form.stats || []).map((st, i) => (
              <div key={i} className="flex gap-2 items-center" data-testid={`ambassador-stat-${i}`}>
                <input value={st.label || ""} onChange={(e) => setStat(i, "label", e.target.value)} className={`${SEL} sm:w-1/3`} placeholder="Label" maxLength={40} data-testid={`ambassador-stat-label-${i}`} />
                <input value={st.value || ""} onChange={(e) => setStat(i, "value", e.target.value)} className={`${SEL} flex-1`} placeholder="Value" maxLength={80} data-testid={`ambassador-stat-value-${i}`} />
                <button type="button" onClick={() => removeStat(i)} title="Remove stat" className="text-muted-foreground hover:text-blaze transition-colors shrink-0" data-testid={`ambassador-stat-remove-${i}`}><X className="w-4 h-4" /></button>
              </div>
            ))}
            {(form.stats || []).length < 4 && (
              <button type="button" onClick={addStat} className="font-mono text-[11px] uppercase tracking-wider text-olive-bright hover:text-volt transition-colors inline-flex items-center gap-1" data-testid="ambassador-stat-add"><Plus className="w-3.5 h-3.5" /> Add stat</button>
            )}
          </div>
        </div>

        {/* Linked account */}
        <div>
          <label className={LBL}>Linked OpticSwap account <span className="text-muted-foreground normal-case">— optional · search all accounts. Links the public ID-verified badge once they sign up &amp; verify.</span></label>
          <LinkedAccountPicker value={form.linked_username} initial={initial} onChange={(u) => set("linked_username", u)} />
        </div>

        <label className="flex items-center gap-2 font-body text-sm text-secondary-foreground cursor-pointer" data-testid="ambassador-rights-label">
          <input type="checkbox" checked={form.photo_rights_confirmed} onChange={(e) => set("photo_rights_confirmed", e.target.checked)} data-testid="ambassador-rights" />
          Photo rights confirmed <span className="text-[#E8C46A] font-mono text-[10px]">*required to publish</span>
        </label>

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-sniper">
          <button onClick={save} disabled={busy} className={`${BTN} bg-olive text-white hover:bg-olive-bright`} data-testid="ambassador-save">
            <Save className="w-3 h-3 inline mr-1 -mt-0.5" /> {isNew ? "Create draft" : "Save changes"}
          </button>
          {!isNew && initial.status !== "published" && (
            <button onClick={() => changeStatus("published")} disabled={busy || missing.length > 0} title={missing.length ? `Missing: ${missing.join(", ")}` : "Publish"} className={`${BTN} bg-[#4E8A1F] text-white hover:opacity-90`} data-testid="ambassador-publish">
              <Eye className="w-3 h-3 inline mr-1 -mt-0.5" /> Publish
            </button>
          )}
          {!isNew && initial.status === "published" && (
            <button onClick={() => changeStatus("draft")} disabled={busy} className={`${BTN} border border-sniper text-foreground hover:border-olive-bright`} data-testid="ambassador-unpublish">Unpublish (draft)</button>
          )}
          {!isNew && initial.status !== "retired" && (
            <button onClick={() => changeStatus("retired")} disabled={busy} className={`${BTN} border border-sniper text-muted-foreground hover:text-foreground`} data-testid="ambassador-retire">Retire</button>
          )}
          <button onClick={del} disabled={busy} className={`${BTN} border border-blaze/50 text-blaze hover:bg-blaze/10 ml-auto`} data-testid="ambassador-delete"><Trash2 className="w-3 h-3 inline mr-1 -mt-0.5" /> Delete</button>
        </div>
        {!isNew && missing.length > 0 && (
          <p className="font-mono text-[10px] text-[#E8C46A]" data-testid="ambassador-missing">Needs before publish: {missing.join(" · ")}</p>
        )}
      </div>
    </div>
  );
}

function SpecialtyManager({ specialties, usage, onSaved }) {
  const [open, setOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState(null); // specialty being renamed
  const [editVal, setEditVal] = useState("");
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState(null); // { name, in_use_by: [] }

  const add = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try { await api.post("/admin/ambassador-specialties/add", { name }); toast.success(`Added "${name}"`); setNewName(""); onSaved(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const startEdit = (s) => { setEditing(s); setEditVal(s); setBlocked(null); };
  const rename = async () => {
    const to = editVal.trim();
    if (!to || to === editing) { setEditing(null); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/ambassador-specialties/rename", { from: editing, to });
      toast.success(data.affected ? `Renamed — ${data.affected} ambassador(s) updated` : "Renamed");
      setEditing(null); onSaved();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async (s) => {
    const users = usage[s] || [];
    if (users.length) { setBlocked({ name: s, in_use_by: users }); return; }
    setBusy(true);
    try { await api.post("/admin/ambassador-specialties/delete", { name: s }); toast.success(`Deleted "${s}"`); onSaved(); }
    catch (err) {
      const d = err.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "specialty_in_use") setBlocked({ name: s, in_use_by: d.in_use_by || [] });
      else toast.error(formatError(d));
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-sniper bg-surface" data-testid="ambassador-specialty-editor">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-4 py-2.5 text-left">
        <span className="font-mono text-[11px] uppercase tracking-[1.5px] text-secondary-foreground">Editable specialty list ({specialties.length})</span>
        <span className="font-mono text-[10px] text-olive-bright" data-testid="ambassador-specialty-toggle">{open ? "Close" : "Manage"}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          <div className="flex gap-2">
            <input value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
              placeholder="Add a specialty…" className={SEL} data-testid="ambassador-specialty-add-input" />
            <button onClick={add} disabled={busy || !newName.trim()} className={`${BTN} bg-olive text-white hover:bg-olive-bright shrink-0`} data-testid="ambassador-specialty-add-btn"><Plus className="w-3 h-3 inline mr-1 -mt-0.5" /> Add</button>
          </div>

          <div className="space-y-1.5" data-testid="ambassador-specialty-list">
            {specialties.map((s) => {
              const count = (usage[s] || []).length;
              const isEditing = editing === s;
              return (
                <div key={s} className="flex items-center gap-2 border border-sniper bg-input px-2.5 py-1.5" data-testid={`ambassador-specialty-item-${s}`}>
                  {isEditing ? (
                    <>
                      <input value={editVal} onChange={(e) => setEditVal(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") rename(); if (e.key === "Escape") setEditing(null); }}
                        autoFocus className="flex-1 bg-obsidian border border-olive/60 outline-none px-2 py-1 text-foreground font-body text-sm" data-testid="ambassador-specialty-rename-input" />
                      <button onClick={rename} disabled={busy} title="Save rename" className="text-[#4E8A1F] hover:opacity-80 shrink-0" data-testid="ambassador-specialty-rename-save"><Check className="w-4 h-4" /></button>
                      <button onClick={() => setEditing(null)} title="Cancel" className="text-muted-foreground hover:text-foreground shrink-0"><X className="w-4 h-4" /></button>
                    </>
                  ) : (
                    <>
                      <span className="flex-1 font-body text-sm text-foreground truncate">{s}</span>
                      {count > 0
                        ? <span className="font-mono text-[9px] uppercase text-olive-bright shrink-0" title={(usage[s] || []).join(", ")} data-testid={`ambassador-specialty-usage-${s}`}>{count} in use</span>
                        : <span className="font-mono text-[9px] uppercase text-muted-foreground shrink-0" data-testid={`ambassador-specialty-usage-${s}`}>unused</span>}
                      <button onClick={() => startEdit(s)} title="Rename" className="text-muted-foreground hover:text-foreground shrink-0" data-testid={`ambassador-specialty-rename-${s}`}><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => del(s)} title="Delete" className="text-muted-foreground hover:text-blaze shrink-0" data-testid={`ambassador-specialty-delete-${s}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </>
                  )}
                </div>
              );
            })}
          </div>

          <p className="font-mono text-[10px] text-muted-foreground">Renaming updates every ambassador using that specialty. Deleting is blocked while any ambassador uses it.</p>

          {blocked && (
            <div className="border border-[#E8C46A]/50 bg-[#E8C46A]/5 p-3 space-y-2" data-testid="ambassador-specialty-blocked">
              <p className="font-mono text-[10px] uppercase text-[#E8C46A] flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Can't delete "{blocked.name}" — {blocked.in_use_by.length} ambassador(s) use it</p>
              <p className="font-body text-xs text-secondary-foreground" data-testid="ambassador-specialty-blocked-names">In use by: {blocked.in_use_by.join(", ")}. Reassign or remove it from those entries first.</p>
              <button onClick={() => setBlocked(null)} className={`${BTN} border border-sniper text-muted-foreground hover:text-foreground`} data-testid="ambassador-specialty-blocked-dismiss">Got it</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Draggable admin roster. Drag a row (or use the up/down arrows) to reorder; the new order is
// saved immediately and drives BOTH this list and the public /ambassadors page (no daily rotation).
// Mirrors the photo-grid reorder interaction in the Editor above.
function RosterList({ items, onEdit, reload }) {
  const [rows, setRows] = useState(items);
  const [dragIndex, setDragIndex] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setRows(items); }, [items]);

  const persist = async (newRows) => {
    setSaving(true);
    try {
      await api.post("/admin/ambassadors/reorder", { order: newRows.map((r) => r.ambassador_id) });
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Couldn't save the new order.");
      reload();
    } finally { setSaving(false); }
  };

  const move = (from, to) => {
    if (from == null || to < 0 || to >= rows.length || from === to) return;
    const next = [...rows];
    const [x] = next.splice(from, 1);
    next.splice(to, 0, x);
    setRows(next);
    persist(next);
  };
  const onDrop = (to) => { move(dragIndex, to); setDragIndex(null); };

  if (rows.length === 0) {
    return (
      <div className="space-y-2" data-testid="ambassador-list">
        <div className="border border-dashed border-sniper p-8 text-center font-mono text-xs uppercase text-muted-foreground" data-testid="ambassador-empty">No ambassadors yet — create a draft to begin.</div>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="ambassador-list">
      <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[1px] text-muted-foreground">
        <GripVertical className="w-3 h-3" /> Drag a row or use the arrows to set the roster order — this is the exact order the public Ambassadors page shows.
        {saving && <span className="text-olive-bright inline-flex items-center gap-1" data-testid="ambassador-order-saving"><Loader2 className="w-3 h-3 animate-spin" /> saving…</span>}
      </div>
      {rows.map((a, i) => (
        <div key={a.ambassador_id}
          draggable
          onDragStart={() => setDragIndex(i)}
          onDragOver={(e) => e.preventDefault()}
          onDrop={() => onDrop(i)}
          onDragEnd={() => setDragIndex(null)}
          data-testid={`ambassador-roworder-${a.ambassador_id}`}
          className={`border bg-surface transition-colors flex items-center gap-2 p-3 ${dragIndex === i ? "opacity-40 border-olive-bright" : "border-sniper"}`}>
          <span className="cursor-move text-muted-foreground hover:text-foreground shrink-0" title="Drag to reorder" data-testid={`ambassador-drag-${a.ambassador_id}`}>
            <GripVertical className="w-4 h-4" />
          </span>
          <div className="flex flex-col shrink-0">
            <button type="button" onClick={(e) => { e.stopPropagation(); move(i, i - 1); }} disabled={i === 0} title="Move up"
              data-testid={`ambassador-move-up-${a.ambassador_id}`}
              className="text-muted-foreground hover:text-olive-bright disabled:opacity-30 disabled:cursor-not-allowed transition-colors"><ChevronUp className="w-4 h-4" /></button>
            <button type="button" onClick={(e) => { e.stopPropagation(); move(i, i + 1); }} disabled={i === rows.length - 1} title="Move down"
              data-testid={`ambassador-move-down-${a.ambassador_id}`}
              className="text-muted-foreground hover:text-olive-bright disabled:opacity-30 disabled:cursor-not-allowed transition-colors"><ChevronDown className="w-4 h-4" /></button>
          </div>
          <button type="button" onClick={() => onEdit(a)} data-testid={`ambassador-row-${a.ambassador_id}`}
            className="text-left flex items-center gap-3 flex-1 min-w-0 hover:opacity-90 transition-opacity">
            <div className="w-12 h-14 bg-obsidian border border-sniper overflow-hidden shrink-0 relative">
              {a.photos?.[0]
                ? <img src={a.photos[0]} alt="" className="absolute inset-0 w-full h-full object-cover" />
                : <span className="absolute inset-0 flex items-center justify-center font-mono text-[8px] text-muted-foreground">NO PIC</span>}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="font-heading uppercase tracking-tight text-foreground truncate">{a.display_name || "(unnamed)"}</span>
                {a.entry_type === "team" && <Users className="w-3.5 h-3.5 text-olive-bright shrink-0" />}
                <span className={`font-mono text-[9px] uppercase px-1.5 py-0.5 border ${STATUS_TONE[a.status] || STATUS_TONE.draft}`}>{a.status}</span>
              </div>
              <div className="font-mono text-[10px] uppercase text-muted-foreground truncate mt-0.5">
                {a.location || "—"} · {(a.specialties || []).join(", ") || "no specialties"} · {a.photos?.length || 0} photos
              </div>
            </div>
          </button>
          {a.status !== "published" && a.missing_for_publish?.length > 0 && (
            <span className="font-mono text-[9px] uppercase text-[#E8C46A] shrink-0 hidden sm:inline">{a.missing_for_publish.length} to publish</span>
          )}
          {a.account_pending && (() => {
            const reason = !a.linked_username ? "no account linked"
              : !a.linked_exists ? "account not found"
              : "ID not verified";
            const title = !a.linked_username ? "No OpticSwap account linked yet — chase the sign-up"
              : !a.linked_exists ? `No account matches @${a.linked_username} in this environment (it may only exist in production)`
              : `@${a.linked_username} is linked but hasn't completed ID verification`;
            return (
              <span className="font-mono text-[9px] uppercase text-[#E8C46A] border border-[#E8C46A]/50 px-1.5 py-0.5 shrink-0 inline-flex items-center gap-1" title={title} data-testid={`ambassador-account-pending-${a.ambassador_id}`}>
                <ShieldAlert className="w-3 h-3" /> {reason}
              </span>
            );
          })()}
          {a.linked_verified && <ShieldCheck className="w-4 h-4 text-[#4E8A1F] shrink-0" data-testid={`ambassador-verified-flag-${a.ambassador_id}`} />}
        </div>
      ))}
    </div>
  );
}

export default function AdminAmbassadors() {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null); // ambassador object or {} for new

  const load = async () => {
    try { setData((await api.get("/admin/ambassadors")).data); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const onSaved = (doc) => { setEditing(null); load(); };

  if (!data) return <div className="p-6 font-mono text-xs uppercase text-muted-foreground">Loading…</div>;

  // Specialty usage (name -> [display names]) computed from the loaded roster; powers the
  // in-use counts and the delete-in-use guard in the manager. Backend enforces on delete too.
  const specialtyUsage = {};
  (data.ambassadors || []).forEach((a) => (a.specialties || []).forEach((s) => {
    (specialtyUsage[s] = specialtyUsage[s] || []).push(a.display_name || "(unnamed)");
  }));

  return (
    <div className="p-4 sm:p-6 max-w-[1000px]" data-testid="admin-ambassadors">
      <div className="flex items-center gap-3 mb-1">
        <Award className="w-5 h-5 text-olive-bright" />
        <h1 className="font-heading text-2xl uppercase tracking-tight">Ambassadors</h1>
      </div>
      <p className="text-sm text-secondary-foreground font-body mb-5">
        Invite-only, hand-picked roster. The public page stays on the pitch until{" "}
        <b className="text-foreground">{data.min_published} are published</b>{" "}
        — {data.roster_live ? <span className="text-[#4E8A1F]">roster is LIVE</span> : <span className="text-[#E8C46A]">{data.counts.published}/{data.min_published} published</span>}.
      </p>

      <div className="flex items-center gap-2 mb-4">
        <button onClick={() => setEditing({})} className={`${BTN} bg-olive text-white hover:bg-olive-bright`} data-testid="ambassador-new">
          <Plus className="w-3 h-3 inline mr-1 -mt-0.5" /> New ambassador
        </button>
        <span className="font-mono text-[10px] uppercase text-muted-foreground ml-2">
          {data.counts.published} published · {data.counts.draft} draft · {data.counts.retired} retired
        </span>
      </div>

      <div className="mb-5"><SpecialtyManager specialties={data.specialties || []} usage={specialtyUsage} onSaved={load} /></div>

      {editing && (
        <div className="mb-6">
          <Editor initial={editing.ambassador_id ? editing : { ...EMPTY }} specialties={data.specialties || []} onSaved={onSaved} onClose={() => setEditing(null)} />
        </div>
      )}

      <RosterList items={data.ambassadors} onEdit={setEditing} reload={load} />
    </div>
  );
}
